/** Browser and Next.js custom-event boundary. Unknown properties and content containers fail closed.
 * Native campaign values remain separate from minimized URLs.
 */
import { scrubString } from "../monitoring/pii"
import { MESSAGE_FEEDBACK_CATEGORIES, MESSAGE_FEEDBACK_NORMALIZED_CATEGORIES } from "./engagement-freshness"

const routes = new Set([
  "/", "/login", "/register", "/forgot-password", "/reset-password",
  "/auth/callback", "/api/auth/callback", "/chat", "/ai-chat", "/create",
  "/generate", "/generate/image", "/explore", "/support", "/affiliate",
  "/settings", "/settings/account", "/settings/privacy", "/settings/profile",
  "/settings/subscription", "/pricing", "/subscription", "/trust", "/legal",
  "/age-verification", "/blog", "/blog/archive",
])

export function minimizePath(input: string): string {
  const path = input.split(/[?#]/, 1)[0].replace(/\/+$/, "") || "/"
  const localized = path.match(/^\/(en|fr|es|de|it|pt|ja|ko|zh|ru|uk|nl|pl|tr|sv|id)(\/.*)?$/)
  if (localized) return `/${localized[1]}${minimizePath(localized[2] || "/")}`.replace(/\/$/, "")
  if (routes.has(path)) return path
  // Public editorial slugs remain useful for blog-assistance reports.
  if (/^\/(blog|blog-author|legal)\/[a-z0-9-]{1,160}$/.test(path)) return path
  for (const prefix of ["/chat/", "/profile/", "/explore/", "/blog/", "/blog-author/", "/legal/"]) {
    if (path.startsWith(prefix)) return `${prefix}:id`
  }
  if (path.startsWith("/admin/")) return "/admin/:path"
  return "/:other"
}

export function minimizeUrl(input: string, originOnly = false): string {
  if (input === "$direct" || input === "") return input
  try {
    const absolute = /^[a-z][a-z\d+.-]*:/i.test(input) || input.startsWith("//")
    const url = new URL(input, "https://analytics.invalid")
    if (url.protocol !== "https:" && url.protocol !== "http:") return "[redacted]"
    const path = originOnly ? "" : minimizePath(url.pathname)
    return absolute ? `${url.origin}${path}` : path
  } catch { return "[redacted]" }
}

const urlKeys = new Set([
  "$current_url", "$initial_current_url", "$session_entry_url", "url", "page_url",
  "$referrer", "$initial_referrer", "$session_entry_referrer", "initial_referrer",
])
const pathKeys = new Set([
  "$pathname", "$initial_pathname", "$session_entry_pathname", "page_path", "route_path",
  "initial_landing_path", "path", "pathname",
])
// No wildcard for arbitrary SDK properties, free text, traits or nested objects.
// posthog-js `$web_vitals` numeric metric values (`$web_vitals_<NAME>_value`). The sibling
// `_event` objects stay dropped: they are nested and carry navigation ids.
const webVitalValueKey = /^\$web_vitals_(LCP|INP|CLS|FCP|TTFB)_value$/
const scalarKeys = new Set(`
  distinct_id $device_id $user_id $anon_distinct_id $session_id $window_id $pageview_id
  $insert_id $time $lib $lib_version $host $initial_host $session_entry_host
  $os $os_version $browser $browser_version $browser_language $browser_language_prefix
  $device_type $timezone $timezone_offset $screen_height $screen_width
  $viewport_height $viewport_width $referring_domain $initial_referring_domain
  $session_entry_referring_domain $is_identified $process_person_profile $event_type
  $feature_flag $feature_flag_response $feature_flag_reason
  $exception_source $exception_level $exception_handled
  method was_new_user source emit_source environment is_synthetic is_internal is_test
  is_sandbox is_first_purchase subscription current_plan plan tier billing_interval
  billing_period amount_cents currency checkout_attempt_id stripe_event_id
  modal field surface context outcome stage error_stage error_code blocked_reason
  step step_name step_number total_steps steps_completed slide destination
  character_id conversation_id message_id message_type message_length
  is_new_conversation is_image_intent response_latency_ms conversation_depth_before_send
  conversation_depth_after_receive session_message_index memory_context_used memory_context_count
  sentiment has_negative_freshness_feedback ui_locale chat_language voice_language
  scope coins threshold tab post_id loaded_pages content_type content_id action
  style aspect_ratio has_negative_prompt prompt_modifier_count generation_id status
  has_custom_backstory gate trigger intent remaining_messages_bucket messages_sent_count
  flag variant paywall_experiment_flag paywall_experiment_variant selected_from
  checkout_reassurance_flag checkout_reassurance_variant
  confidence_strip_variant default_is_yearly had_subscription days_since_signup
  has_conversations onboarding_completed is_activated trakdesk_cid
  authenticated intercom feedback_length feedback_category cancellation_mode $survey_id
  inferred_amount_cents git_commit pipeline_phase failure_class failure_kind query_namespace mutation_namespace
  utm_source utm_medium utm_campaign utm_content utm_term utm_id gclid fbclid msclkid
  initial_utm_source initial_utm_medium initial_utm_campaign initial_utm_content initial_utm_term initial_utm_id
  $initial_utm_source $initial_utm_medium $initial_utm_campaign $initial_utm_content $initial_utm_term
  ts_click_id ts_session_id ts_creative ts_site ts_zone ts_adspot ts_device ts_os
  ts_browser ts_carrier ts_geo ts_lang ts_pricing ts_cost experiment_id angle
  $raw_user_agent is_suspected_bot bot_signals
`.trim().split(/\s+/))

// Arrays of closed enum values (chat:message_feedback_submit reason chips). Only listed values
// survive; anything else (free text, objects, unknown strings) is dropped.
const enumArrayKeys = new Map<string, ReadonlySet<string>>([
  ["categories", new Set<string>(MESSAGE_FEEDBACK_CATEGORIES)],
  ["normalized_categories", new Set<string>(MESSAGE_FEEDBACK_NORMALIZED_CATEGORIES)],
])

function minimizeEnumArray(value: unknown, allowed: ReadonlySet<string>): string[] | undefined {
  if (!Array.isArray(value)) return undefined
  return [...new Set(value.filter((item): item is string => typeof item === "string" && allowed.has(item)))]
}

/** PostHog sets $virt_is_bot from this property. Keep a caller value; otherwise
 * attach the request or browser user agent. Empty means absent. */
export function withRawUserAgent(
  properties: Record<string, unknown>,
  userAgent: string | null | undefined,
): Record<string, unknown> {
  if (typeof properties.$raw_user_agent === "string" && properties.$raw_user_agent.length > 0) {
    return properties
  }
  const ua = userAgent?.trim()
  if (!ua) return properties
  return { ...properties, $raw_user_agent: ua.slice(0, 256) }
}

export function minimizeAnalyticsProperties(props: Record<string, unknown>, sdkTransport = false): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(props)) {
    if (sdkTransport && key === "token" && typeof value === "string") {
      // SDK overwrites this field with its public project key before before_send.
      result[key] = value
    } else if (urlKeys.has(key) && typeof value === "string") {
      result[key] = minimizeUrl(value, key.includes("referrer"))
    } else if (pathKeys.has(key) && typeof value === "string") {
      result[key] = minimizePath(value)
    } else if ((key === "$set" || key === "$set_once") && value && typeof value === "object" && !Array.isArray(value)) {
      result[key] = minimizeAnalyticsProperties(value as Record<string, unknown>)
    } else if (key === "$exception_fingerprint" && typeof value === "string") {
      // Grouping key only: opaque identifiers, never free text.
      if (/^[A-Za-z0-9][A-Za-z0-9_.:-]{0,255}$/.test(value)) result[key] = value
    } else if (enumArrayKeys.has(key)) {
      const kept = minimizeEnumArray(value, enumArrayKeys.get(key)!)
      if (kept) result[key] = kept
    } else if (webVitalValueKey.test(key)) {
      // Numeric telemetry only: drop strings, booleans, null and non-finite numbers.
      if (typeof value === "number" && Number.isFinite(value)) result[key] = value
    } else if (scalarKeys.has(key) || /^\$feature\/[a-z0-9_-]+$/.test(key)) {
      if (typeof value === "string") result[key] = scrubString(value).slice(0, 256)
      else if (typeof value === "boolean" || value === null) result[key] = value
      else if (typeof value === "number" && Number.isFinite(value)) result[key] = value
    }
  }
  return result
}

type FlagPrivacyClient = {
  get_property?: (key: string) => unknown
  resetPersonPropertiesForFlags?: (reload?: boolean) => void
  setPersonPropertiesForFlags?: (props: Record<string, unknown>, reload?: boolean) => void
}

/** Flag evaluation has a separate request path, outside before_send.
 * These storage keys are pinned SDK 1.434.7 contracts, covered by transport tests.
 * Override URL/search fields for flags only; native acquisition persistence stays intact.
 */
export function minimizeFlagEvaluationProperties(client: FlagPrivacyClient): void {
  if (!client.setPersonPropertiesForFlags || !client.resetPersonPropertiesForFlags) return
  const stored = client.get_property?.("$stored_person_properties")
  const safe = stored && typeof stored === "object" && !Array.isArray(stored)
    ? minimizeAnalyticsProperties(stored as Record<string, unknown>) : {}
  client.resetPersonPropertiesForFlags(false)
  client.setPersonPropertiesForFlags({
    ...safe,
    $initial_current_url: "[redacted]",
    $initial_pathname: "[redacted]",
    $initial_referrer: "[redacted]",
    $initial_search_keyword: "[redacted]",
  }, false)
}

/** Keep code locations, without exception messages, source context or raw stacks. */
export function minimizeExceptionList(value: unknown): Record<string, unknown>[] {
  if (!Array.isArray(value)) return []
  return value.slice(0, 5).map(item => {
    const exception = item && typeof item === "object" ? item : {}
    const frames = Array.isArray(exception.stacktrace?.frames) ? exception.stacktrace.frames : []
    return {
      type: /^(Error|TypeError|RangeError|ReferenceError|SyntaxError|URIError|SecurityError|UnhandledRejection|GenerationPipelineError)$/.test(exception.type)
        ? exception.type : "Error",
      value: "[redacted]",
      // PostHog's error-tracking parser needs `type: "raw"` and a `function` on every frame;
      // without `function` the event is rejected ("missing field `function`", PLE-917).
      // "?" is the SDK's own placeholder, so no real function name leaves the browser.
      stacktrace: { type: "raw", frames: frames.filter((frame: unknown) => frame && typeof frame === "object").slice(0, 50).map((frame: Record<string, unknown>) => ({
        ...(typeof frame.filename === "string" && /\/_next\/static\/[a-zA-Z0-9_./-]+\.js(?:[?#]|$)/.test(frame.filename)
          ? { filename: frame.filename.match(/\/_next\/static\/[a-zA-Z0-9_./-]+\.js(?:[?#]|$)/)?.[0].replace(/[?#]$/, "") } : {}),
        ...(typeof frame.lineno === "number" ? { lineno: frame.lineno } : {}),
        ...(typeof frame.colno === "number" ? { colno: frame.colno } : {}),
        ...(typeof frame.in_app === "boolean" ? { in_app: frame.in_app } : {}),
        function: "?",
        // PostHog error tracking rejects frames without `platform` (PLE-917). SDK constant, never user data.
        ...(typeof frame.platform === "string" && /^[a-z]+:[a-z]+$/.test(frame.platform) ? { platform: frame.platform } : {}),
      })) },
    }
  })
}
